import type { VisionPlan } from "@sitescene/types";

import { COCO_CLASS_NAMES } from "@sitescene/constant";
import { VisionCountMode } from "@sitescene/types";
import { XClose } from "@untitledui/icons";

import { Button } from "@/components/ui/buttons/button";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { Input } from "@/components/ui/input/input";
import { NativeSelect } from "@/components/ui/select/select-native";
import { classColor } from "@/features/vision-lab/format";

export type DrawMode = "area" | "line" | "point";

interface PlanEditorProps {
	drawing: DrawMode | null;
	onChange: (plan: VisionPlan) => void;
	onDraw: (mode: DrawMode | null) => void;
	plan: VisionPlan;
}

const COUNT_OPTIONS = [
	{ label: "No count", value: "" },
	{ label: "Unique tracks", value: VisionCountMode.Unique },
	{ label: "Crossing a line", value: VisionCountMode.Line },
	{ label: "Inside an area", value: VisionCountMode.Area },
];

/**
 * The plan before it runs: what RF-DETR looks for, what's counted and how,
 * what Gemini describes, and what Gemini looks for itself. Lines and areas are
 * drawn on the video.
 */
export const PlanEditor = ({
	drawing,
	onChange,
	onDraw,
	plan,
}: PlanEditorProps) => {
	const detect = plan.detect.classes;
	const setDetect = (classes: string[]) =>
		onChange({
			...plan,
			...(plan.count && {
				count: {
					...plan.count,
					classes: plan.count.classes.filter((name) => classes.includes(name)),
				},
			}),
			...(plan.describe && {
				describe: {
					...plan.describe,
					classes: plan.describe.classes.filter((name) =>
						classes.includes(name),
					),
				},
			}),
			detect: { classes },
		});

	const setCountMode = (mode: "" | VisionCountMode) => {
		onDraw(null);
		if (!mode) {
			const { count: _count, ...rest } = plan;
			onChange(rest);
			return;
		}
		onChange({
			...plan,
			count: {
				classes: plan.count?.classes.length ? plan.count.classes : detect,
				mode,
			},
		});
	};

	const toggle = (list: string[], name: string, on: boolean) =>
		on ? [...new Set([...list, name])] : list.filter((item) => item !== name);

	const targets = plan.geminiTargets ?? [];
	const setTargets = (next: NonNullable<VisionPlan["geminiTargets"]>) => {
		const { geminiTargets: _targets, ...rest } = plan;
		onChange(next.length ? { ...rest, geminiTargets: next } : rest);
	};

	const needsShape =
		(plan.count?.mode === VisionCountMode.Line && !plan.count.line) ||
		(plan.count?.mode === VisionCountMode.Area && !plan.count.area);

	return (
		<div className="flex flex-col gap-4 text-sm">
			{plan.explanation && <p className="text-secondary">{plan.explanation}</p>}

			<section className="flex flex-col gap-2">
				<h4 className="font-medium text-primary">RF-DETR looks for</h4>
				<div className="flex flex-wrap gap-1.5">
					{detect.map((name) => (
						<span
							className="flex items-center gap-1 rounded-full border px-2 py-0.5"
							key={name}
							style={{ borderColor: classColor(name) }}
						>
							{name}
							<button
								aria-label={`Remove ${name}`}
								className="text-tertiary hover:text-primary"
								onClick={() =>
									setDetect(detect.filter((item) => item !== name))
								}
								type="button"
							>
								<XClose className="size-3" />
							</button>
						</span>
					))}
					{!detect.length && (
						<span className="text-tertiary">Nothing: Gemini only</span>
					)}
				</div>
				<NativeSelect
					aria-label="Add a class"
					onChange={(event) => {
						if (event.target.value) setDetect([...detect, event.target.value]);
					}}
					options={[
						{ label: "Add one of the 80 classes…", value: "" },
						...COCO_CLASS_NAMES.filter((name) => !detect.includes(name)).map(
							(name) => ({
								label: name,
								value: name,
							}),
						),
					]}
					value=""
				/>
			</section>

			<section className="flex flex-col gap-2">
				<h4 className="font-medium text-primary">Count</h4>
				<NativeSelect
					aria-label="Count"
					onChange={(event) =>
						setCountMode(event.target.value as "" | VisionCountMode)
					}
					options={COUNT_OPTIONS}
					value={plan.count?.mode ?? ""}
				/>
				{plan.count && (
					<>
						<div className="flex flex-wrap gap-3">
							{detect.map((name) => (
								<Checkbox
									isSelected={plan.count?.classes.includes(name)}
									key={name}
									label={name}
									onChange={(on) =>
										plan.count &&
										onChange({
											...plan,
											count: {
												...plan.count,
												classes: toggle(plan.count.classes, name, on),
											},
										})
									}
								/>
							))}
						</div>
						{(plan.count.mode === VisionCountMode.Line ||
							plan.count.mode === VisionCountMode.Area) && (
							<div className="flex flex-wrap items-center gap-2">
								<Button
									color={drawing ? "primary" : "secondary"}
									onClick={() =>
										onDraw(
											drawing
												? null
												: plan.count?.mode === VisionCountMode.Line
													? "line"
													: "area",
										)
									}
									size="xs"
								>
									{drawing
										? "Cancel drawing"
										: plan.count.line || plan.count.area
											? `Redraw the ${plan.count.mode}`
											: `Draw the ${plan.count.mode} on the video`}
								</Button>
								{drawing === "line" && (
									<span className="text-tertiary">
										Click two points on the video.
									</span>
								)}
								{drawing === "area" && (
									<span className="text-tertiary">
										Click the corners, then Finish under the video.
									</span>
								)}
								{!drawing && needsShape && (
									<span className="text-warning-primary">
										Draw it before running.
									</span>
								)}
								{plan.count.mode === VisionCountMode.Line &&
									plan.count.line &&
									!drawing && (
										<span className="text-tertiary">
											The arrow shows "in": crossing from its left to its right,
											facing from the first point to the second.
										</span>
									)}
							</div>
						)}
					</>
				)}
			</section>

			<section className="flex flex-col gap-2">
				<Checkbox
					isDisabled={!detect.length}
					isSelected={Boolean(plan.describe)}
					label="Gemini answers a question about each object"
					onChange={(on) => {
						const { describe: _describe, ...rest } = plan;
						onChange(
							on
								? {
										...rest,
										describe: {
											classes: detect,
											question: "What does it look like?",
										},
									}
								: rest,
						);
					}}
				/>
				{plan.describe && (
					<>
						<Input
							label="Question for each object"
							onChange={(question) =>
								plan.describe &&
								onChange({ ...plan, describe: { ...plan.describe, question } })
							}
							value={plan.describe.question}
						/>
						<div className="flex flex-wrap gap-3">
							{detect.map((name) => (
								<Checkbox
									isSelected={plan.describe?.classes.includes(name)}
									key={name}
									label={name}
									onChange={(on) =>
										plan.describe &&
										onChange({
											...plan,
											describe: {
												...plan.describe,
												classes: toggle(plan.describe.classes, name, on),
											},
										})
									}
								/>
							))}
						</div>
						<div className="flex flex-col gap-2 rounded-md bg-secondary px-3 py-2">
							<p className="text-secondary">
								<span className="font-medium text-primary">Focus point</span>{" "}
								(optional): click the spot that matters, such as a badge reader.
								Gemini then watches a short clip of each object as it comes near
								that spot, instead of three still crops, so it can see actions
								like a card held to a reader.
							</p>
							<div className="flex flex-wrap items-center gap-2">
								<Button
									color={drawing === "point" ? "primary" : "secondary"}
									onClick={() => onDraw(drawing === "point" ? null : "point")}
									size="xs"
								>
									{drawing === "point"
										? "Cancel"
										: plan.focusPoint
											? "Move the focus point"
											: "Place a focus point"}
								</Button>
								{plan.focusPoint && drawing !== "point" && (
									<Button
										color="tertiary"
										onClick={() => {
											const { focusPoint: _focus, ...rest } = plan;
											onChange(rest);
										}}
										size="xs"
									>
										Remove
									</Button>
								)}
								{drawing === "point" && (
									<span className="text-tertiary">
										Click the spot on the video.
									</span>
								)}
							</div>
						</div>
					</>
				)}
			</section>

			<section className="flex flex-col gap-2">
				<h4 className="font-medium text-primary">
					Gemini looks for (not one of the 80 classes)
				</h4>
				{targets.length > 0 && (
					<p className="bg-warning-50 text-warning-700 rounded-md px-3 py-2">
						RF-DETR can't detect these, so Gemini looks for them on frames
						sampled every few seconds. It's slower, and its boxes are coarser
						and drawn dashed.
					</p>
				)}
				{targets.map((target, index) => (
					<div className="flex items-end gap-2" key={index}>
						<Input
							className="w-32"
							label="Label"
							onChange={(label) =>
								setTargets(
									targets.map((t, i) => (i === index ? { ...t, label } : t)),
								)
							}
							value={target.label}
						/>
						<Input
							className="flex-1"
							label="What to look for"
							onChange={(description) =>
								setTargets(
									targets.map((t, i) =>
										i === index ? { ...t, description } : t,
									),
								)
							}
							value={target.description}
						/>
						<Button
							aria-label={`Remove ${target.label}`}
							color="tertiary"
							iconLeading={XClose}
							onClick={() => setTargets(targets.filter((_, i) => i !== index))}
							size="sm"
						/>
					</div>
				))}
				{targets.length < 5 && (
					<div>
						<Button
							color="secondary"
							onClick={() =>
								setTargets([...targets, { description: "", label: "" }])
							}
							size="xs"
						>
							Add something for Gemini to look for
						</Button>
					</div>
				)}
			</section>
		</div>
	);
};
